<template>
  <div
    class="canvas-paint_container"
    :class="{ active }"
  >
    <div
      :class="{ active, hideControls }"
      class="canvas-paint_controls text-noselect"
    >
      <GwMessage
        class="no-events"
        :message="{
          hide: true,
        }"
        style="
          --clr-back: var(--clr-yellow);
          --back-padding: 0.25rem 2rem;
          --back-opacity: 0.75;
          top: 1rem;
          position: relative;
        "
      >
        <template #icon>
          <i
            class="mdi mdi-pencil text-large"
            :style="'color:var(--clr-white)'"
          ></i>
        </template>
        <template #default>
          <div class="weight_500">
            {{ $t('canvas.title') }}
          </div>

          <div
            class="clr-grey ml-4"
            v-html="
              $t('canvas.press_key_exit', {
                key: hotkeys.drawing_mode.displayCombination,
              })
            "
          ></div>

          <div class="canvas-paint_buttons">
            <button
              :title="$t('canvas.undo')"
              class="canvas-paint_button"
              :disabled="undoStack.length === 0"
              @mousedown.stop
              @click="undo"
            >
              <i class="mdi mdi-undo"></i>
            </button>
            <button
              :title="$t('canvas.redo')"
              class="canvas-paint_button"
              :disabled="redoStack.length === 0"
              @mousedown.stop
              @click="redo"
            >
              <i class="mdi mdi-redo"></i>
            </button>
          </div>
        </template>
      </GwMessage>
    </div>
    <div
      v-if="active"
      class="canvas-paint"
      data-gwmenu="noclose"
      @click.stop
    >
      <div class="canvas-paint_controls_overlay"></div>
      <canvas
        v-if="active"
        id="sheet_painter"
        :width="viewport.width"
        :height="viewport.height"
      ></canvas>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import { LazyBrush } from 'lazy-brush'
import GwMessage from '@/components/Messages/GwMessage'

export default {
  name: 'CanvasPaint',
  components: {
    GwMessage,
  },
  data() {
    return {
      brush: new LazyBrush({
        radius: 1,
        enabled: true,
        initialPoint: { x: 0, y: 0 },
      }),
      startPoint: { x: 0, y: 0 },
      undoStack: [],
      redoStack: [],
      active: false,
      activated: false,
      canvas: null,
      context: null,
      paint: null,
      hideControlsTimeout: null,
      hideControls: false,
      isMac: navigator.userAgent.includes('Macintosh'),
    }
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
    window.removeEventListener('mouseup', this.mouseUpEventHandler)
    window.removeEventListener('mousemove', this.mouseMoveEventHandler)
    window.removeEventListener('mousedown', this.mouseDownEventHandler)
    window.removeEventListener('touchstart', this.touchstartEventHandler)
    window.removeEventListener('touchmove', this.touchMoveEventHandler)
    window.removeEventListener('touchend', this.mouseUpEventHandler)
  },
  mounted() {
    this.initialize()
    window.addEventListener('keydown', this.handleKeydown)
    window.addEventListener('mouseup', this.mouseUpEventHandler)
    window.addEventListener('mousemove', this.mouseMoveEventHandler)
    window.addEventListener('mousedown', this.mouseDownEventHandler)
    window.addEventListener('touchstart', this.touchstartEventHandler)
    window.addEventListener('touchmove', this.touchMoveEventHandler)
    window.addEventListener('touchend', this.mouseUpEventHandler)
  },
  methods: {
    handleKeydown(event) {
      if (!this.active) return

      event.preventDefault()

      const isCtrl = this.isMac ? event.metaKey : event.ctrlKey
      if (isCtrl && event.shiftKey && event.key === 'z') {
        this.redo()
      } else if (isCtrl && event.key === 'z') {
        this.undo()
      }
    },
    initialize() {
      if (this.active) {
        nextTick(() => {
          const canvas = document.getElementById('sheet_painter')
          // --- Hi‑DPI / Retina support ---
          // 1.75 is the optimal trade-off between performance and quality
          const RATIO_MULTIPLIER = 1.75
          const ratio = (window.devicePixelRatio || 1) * RATIO_MULTIPLIER
          canvas.width = this.viewport.width * ratio
          canvas.height = this.viewport.height * ratio
          canvas.style.width = `${this.viewport.width}px`
          canvas.style.height = `${this.viewport.height}px`

          const context = canvas.getContext('2d')
          // Scale so 1 unit in code == 1 CSS pixel on screen
          context.scale(ratio, ratio)

          // Drawing style
          context.strokeStyle = 'yellow'
          context.lineJoin = 'round'
          context.lineCap = 'round'
          context.imageSmoothingEnabled = true
          context.imageSmoothingQuality = 'high'
          context.globalCompositeOperation = 'source-over'
          context.lineWidth = 3
          this.context = context
          this.canvas = canvas
        })
      } else {
        this.context = null
        this.canvas = null
        this.paint = null
      }
    },
    toggle() {
      this.active = !this.active
      this.initialize()
    },
    updateBrushPosition(x, y, both = false) {
      this.brush.update(
        {
          x,
          y,
        },
        { friction: 0.1, both },
      )
    },
    drawNew() {
      const { x, y } = this.brush.getBrushCoordinates()

      if (this.paint) {
        this.context.lineTo(x, y)
        this.context.stroke()
      }
    },
    saveState() {
      if (!this.paint) {
        const imageData = this.context.getImageData(0, 0, this.canvas.width, this.canvas.height)
        this.undoStack.push(imageData)
        if (this.undoStack.length > 10) {
          this.undoStack.shift() // Limit stack size to 10 states
        }
      }
    },
    undo() {
      if (this.undoStack.length > 0) {
        const lastState = this.undoStack.pop()
        this.redoStack.push(this.context.getImageData(0, 0, this.canvas.width, this.canvas.height))
        this.context.putImageData(lastState, 0, 0)
      }
    },
    redo() {
      if (this.redoStack.length > 0) {
        const lastRedoState = this.redoStack.pop()
        this.undoStack.push(this.context.getImageData(0, 0, this.canvas.width, this.canvas.height))
        this.context.putImageData(lastRedoState, 0, 0)
      }
    },
    mouseDownEventHandler(e) {
      if (!this.active) return
      this.saveState()
      this.paint = true
      const x = e.pageX - this.canvas.offsetLeft
      const y = e.pageY - this.canvas.offsetTop
      this.startPoint = { x, y }
      if (this.paint) {
        this.context.beginPath()
        this.updateBrushPosition(x, y, true)
        const { x: lastX, y: lastY } = this.brush.getBrushCoordinates()
        this.context.moveTo(lastX, lastY)
      }
    },
    touchstartEventHandler(e) {
      if (!this.active) return
      this.saveState()
      this.paint = true
      if (this.paint) {
        const x = e.touches[0].pageX - this.canvas.offsetLeft
        const y = e.touches[0].pageY - this.canvas.offsetTop
        this.startPoint = { x, y }
        this.context.beginPath()
        this.updateBrushPosition(x, y, true)
        const { x: lastX, y: lastY } = this.brush.getBrushCoordinates()
        this.context.moveTo(lastX, lastY)
      }
    },
    mouseUpEventHandler(e) {
      if (!this.active) return
      this.context.closePath()
      const x = e.pageX - this.canvas.offsetLeft
      const y = e.pageY - this.canvas.offsetTop

      if (this.startPoint.x === x && this.startPoint.y === y) {
        this.context.arc(x, y, 2, 0, 3 * Math.PI)
        this.context.fillStyle = 'yellow'
        this.context.fill()
      }
      this.paint = false
    },

    mouseMoveEventHandler(e) {
      if (!this.active) return
      const x = e.pageX - this.canvas.offsetLeft
      const y = e.pageY - this.canvas.offsetTop
      if (this.paint) {
        this.updateBrushPosition(x, y)
        this.drawNew()
      }
    },

    touchMoveEventHandler(e) {
      if (!this.active) return
      if (this.paint) {
        const x = e.touches[0].pageX - this.canvas.offsetLeft
        const y = e.touches[0].pageY - this.canvas.offsetTop
        this.updateBrushPosition(x, y)
        this.drawNew()
      }
    },
  },
  computed: {
    viewport() {
      return this.$store.getters.globals_window_size
    },
    hotkeys() {
      return this.$store.getters.hotkeys
    },
    hotkeysPressed() {
      return this.$store.getters.hotkeys_pressed
    },
  },
  watch: {
    active(val) {
      if (val) {
        this.undoStack = []
        this.redoStack = []
        this.hideControlsTimeout = setTimeout(() => {
          this.hideControls = true
        }, 1500)
      } else {
        clearTimeout(this.hideControlsTimeout)
        this.hideControls = false
      }
    },
    hotkeysPressed(keys) {
      if (!keys) return
      const hotkeyActions = {
        drawing_mode_open: {
          keys: this.hotkeys.drawing_mode.keyCombination,
          fn: () => this.toggle(),
        },
        ...(this.active
          ? { drawing_mode_close: { keys: ['escape'], fn: () => this.toggle() } }
          : {}),
      }
      this.$store.dispatch('hotkeys_perform_action', { keys, hotkeyActions })
    },
    viewport() {
      this.initialize()
    },
  },
}
</script>
<style lang="scss" scoped>
.canvas-paint {
  display: flex;
  justify-content: center;

  &_container {
    pointer-events: none;
    z-index: var(--z-index-canvas-paint);
    height: 100vh;
    width: 100vw;
    position: fixed;
    left: 0;
    top: 0;
    display: flex;
    justify-content: center;
    border: 2px solid transparent;
    transition: border-color 0.3s;

    &.active {
      pointer-events: auto;
      border-color: var(--clr-yellow-2);
      cursor:
        url('@/assets/cursors/pencil.svg') 1 18,
        auto;
    }
  }

  &_controls {
    opacity: 0;
    transform: translateY(-100%);
    z-index: 10;
    transition: all 0.3s ease-in-out;
    position: absolute;
    pointer-events: none;

    &.active {
      opacity: 1;
      transform: translateY(0);

      &.hideControls {
        opacity: 0;
        transform: translateY(-100%);
      }
    }

    &:hover.active {
      opacity: 1;
      transform: translateY(0);
    }

    &_overlay {
      height: 10rem;
      width: 100vw;
      position: fixed;
      left: 0;
      top: 0;
    }
  }

  &_container:has(.canvas-paint_controls_overlay:hover) {
    .canvas-paint_controls {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &_buttons {
    display: flex;
    gap: 0.25rem;
    justify-content: center;
  }

  &_button {
    border: none;
    border-radius: 4px;
    padding: 0.5rem;
    cursor: pointer;
    transition: all 0.2s;
    pointer-events: auto !important;

    &:hover:not(:disabled) {
      i {
        color: var(--clr-yellow);
      }
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    i {
      font-size: 1.2rem;
      color: var(--clr-yellow-2);
      transition: all 0.3s;
    }
  }
}
</style>
